import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Stack,
  TablePagination,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveFilterDialog from './dialogs/SaveFilterDialog';
import ResultsPageTour from './onboarding/ResultsPageTour';
import { apiGet, apiPost, authenticatedFetch } from '../utils/apiUtils';
import supabase from '../utils/supabaseClient';
import LoadingOverlay from './common/LoadingOverlay';

/**
 * FilterResults component - displays filtering results and allows export/override functionality
 * Uses forwardRef to expose overrides data to parent components
 */
const FilterResults = forwardRef(function FilterResults({ 
  onReset,
  onBack,
  setLoading,
  filterId,
  filterResults,
  setFilterResults,
  userOverrides,
  setUserOverrides,
  filterProgress, // Pass along from parent component
  setFilterProgress // Pass along from parent component
}, ref) {
  const [tabIndex, setTabIndex] = useState(0);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);
  const [columnFilter, setColumnFilter] = useState('all'); // State for column filter
  const [searchQuery, setSearchQuery] = useState(''); // State for search query
  const [saveDialogOpen, setSaveDialogOpen] = useState(false); // State for save dialog
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [sortBy, setSortBy] = useState('frequency');
  const [sortOrder, setSortOrder] = useState('desc'); // Default to descending order
  const [localOverrides, setLocalOverrides] = useState({}); // Track local overrides separately
  // Store the standardized filter config for passing to SaveFilterDialog
  const [standardizedFilterConfig, setStandardizedFilterConfig] = useState(null);
  // Tutorial state
  const [showTour, setShowTour] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  // RACE CONDITION FIX: Track number of fetch attempts at component level
  const fetchAttemptRef = useRef(0);
  // Removed duplicate declaration of filterProgress state
  
  // Create a function to get formatted overrides
  const getCurrentOverrides = () => {
    // If we don't have any results or user overrides, return empty object
    if (!filterResults || !userOverrides) {
      console.log('No filter results or user overrides available', { filterResults: !!filterResults, userOverrides });
      return {};
    }
    
    console.log('Raw user overrides:', userOverrides);
    
    // Format overrides for storage
    // We need a structure like: { columnName: { value: decision, ... }, ... }
    const formattedOverrides = {};
    
    // Loop through all override entries
    Object.entries(userOverrides).forEach(([key, decision]) => {
      // Key format is "column|value"
      const [column, value] = key.split('|');
      
      // Initialize column object if it doesn't exist
      if (!formattedOverrides[column]) {
        formattedOverrides[column] = {};
      }
      
      // Add the decision for this value
      formattedOverrides[column][value] = decision;
    });
    
    console.log('Formatted overrides for saving:', formattedOverrides);
    return formattedOverrides;
  };

  // Expose methods to parent components using useImperativeHandle
  useImperativeHandle(ref, () => ({
    // Method to get current overrides in the correct format for saving
    getCurrentOverrides
  }));

  // Emergency reset function to clear all application state
  // Function preserved in code but button removed from UI
  // eslint-disable-next-line no-unused-vars
  const handleEmergencyReset = () => {
    // Clear all localStorage data
    localStorage.clear();
    
    // Show alert to inform user
    alert('Application has been reset. You will be redirected to the start page.');
    
    // Redirect to home page
    window.location.href = '/';
  };

  // Fetch results when component mounts or filterId changes (only if filterResults not already provided)
  useEffect(() => {
    if (filterId && !filterResults) {
      fetchResults();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId, filterResults]);
  
  // Create standardized filter config when filter results change
  useEffect(() => {
    if (filterResults) {
      // CRITICAL DEBUGGING: We need to see the EXACT structure of the filter configuration
      console.log('-------------------------------------------------------------');
      console.log('DEBUG - ENTIRE filterResults object:', JSON.stringify(filterResults, null, 2));
      console.log('DEBUG - Looking for column instructions specifically:');
      
      // If filterConfig exists, examine its structure in detail
      if (filterResults.filterConfig) {
        // Check if Title column exists directly at top level
        if (filterResults.filterConfig.Title) {
          console.log('DEBUG - Title found directly at top level:', filterResults.filterConfig.Title);
        }
        
        // Check if column instructions exist in nested columnConfig structure
        if (filterResults.filterConfig.columnConfig && filterResults.filterConfig.columnConfig.Title) {
          console.log('DEBUG - Title found in columnConfig:', filterResults.filterConfig.columnConfig.Title);
        }
        
        // Look at exactly what's in selectedColumns
        if (filterResults.filterConfig.selectedColumns) {
          console.log('DEBUG - selectedColumns contains:', filterResults.filterConfig.selectedColumns);
        }
      }
      
      // IMPORTANT: We need to preserve ONLY the columns that were specifically selected by the user
      // NOT all columns from the dataset
      let columns = new Set();
      
      // Step 1: Get columns from the original filter configuration (these are the ones the user chose)
      if (filterResults.filterConfig && Array.isArray(filterResults.filterConfig.selectedColumns)) {
        filterResults.filterConfig.selectedColumns.forEach(col => columns.add(col));
        console.log('Using columns from original filter config:', filterResults.filterConfig.selectedColumns);
      } 
      // In case the original selected columns are not available, use a fallback
      else if (filterResults.valueDecisions) {
        // Only take columns where we have actual decisions (not empty objects)
        Object.keys(filterResults.valueDecisions).forEach(col => {
          if (Object.keys(filterResults.valueDecisions[col] || {}).length > 0) {
            columns.add(col);
          }
        });
        console.log('Fallback: Using value decision columns');
      }
      
      // Get user overrides - these contain columns explicitly chosen by the user
      const currentOverrides = (ref && ref.current && ref.current.getCurrentOverrides) ? ref.current.getCurrentOverrides() : {};
      
      if (currentOverrides) {
        Object.keys(currentOverrides).forEach(col => columns.add(col));
      }
      
      console.log('Extracted columns from all sources:', Array.from(columns));
      
      // Get reasoning text and model from database data only - no localStorage fallback
      const reasoningText = filterResults?.reasoningText || '';
      const model = filterResults?.model || 'gpt-4o';
      const chunkSize = filterResults?.chunkSize || 300;
      
      if (!filterResults?.reasoningText) {
        console.warn('No reasoningText found in filterResults for save operation (first instance)');
      }
      
      // Convert columns Set to Array
      const columnsArray = Array.from(columns);
      
      // Simplest approach - find the filterToolState in localStorage without relying on user ID
      let localStorageData = {};
      // Look for any filterToolState keys
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('filterToolState_')) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data && data.filterConfig) {
              localStorageData = data;
              break; // Use the first valid filterConfig we find
            }
          } catch (e) {
            console.error('Error parsing localStorage data:', e);
          }
        }
      }
      console.log('--------- CHECKING LOCAL STORAGE ---------');
      if (localStorageData.filterConfig) {
        columnsArray.forEach(column => {
          if (localStorageData.filterConfig[column]) {
            console.log(`LOCAL STORAGE has instructions for ${column}:`, localStorageData.filterConfig[column]);
          }
        });
      }
      
      // Create column config that preserves existing column instructions
      const columnConfig = {};

      // CRITICAL FIX: Use database data as single source of truth for completed jobs
      // For completed filter jobs, qualification criteria should come from database only
      const databaseColumnConfig = filterResults.columnConfig || {};
      
      // DIAGNOSTIC LOG: Check what column instructions were retrieved from database
      console.log('=================== DIAGNOSTIC CHECKPOINT ===================');
      console.log('RETRIEVED FROM DATABASE IN FILTER RESULTS:');
      if (Object.keys(databaseColumnConfig).length > 0) {
        Object.keys(databaseColumnConfig).forEach(column => {
          if (databaseColumnConfig[column] && databaseColumnConfig[column].instructions) {
            console.log(`  - ${column}: "${databaseColumnConfig[column].instructions}"`);
          } else {
            console.log(`  - ${column}: <no instructions>`);
          }
        });
      } else {
        console.log('ERROR: No column configuration found in database for completed job!');
      }
      console.log('============================================================');
      
      // Get column names directly from llmValueDecisions - our single source of truth for columns
      const columnsFromLLM = filterResults.llmValueDecisions ? Object.keys(filterResults.llmValueDecisions) : [];
      console.log('Using columns directly from llmValueDecisions:', columnsFromLLM);
      
      // Build column config using database data as single source of truth
      columnsFromLLM.forEach(column => {
        // Get instructions from database, show error if missing
        if (databaseColumnConfig[column]) {
          columnConfig[column] = databaseColumnConfig[column];
          console.log(`Column ${column} config from database: ${columnConfig[column].instructions ? `"${columnConfig[column].instructions.substring(0, 30)}${columnConfig[column].instructions.length > 30 ? '...' : ''}"` : '<empty>'}`)
        } else {
          columnConfig[column] = { instructions: '' };
          console.log(`WARNING: Column ${column} missing from database configuration - using empty config`);
        }
      });
      
      // Use these columns for the final config
      const columnsToUse = columnsFromLLM.length > 0 ? columnsFromLLM : columnsArray;
      
      // Create standardized filter config
      const config = {
        selectedColumns: columnsToUse,
        reasoningText,
        model,
        chunkSize,
        columnConfig
      };
      
      console.log('Created standardized filter config:', config);
      setStandardizedFilterConfig(config);
    }
  }, [filterResults, userOverrides, ref]);

  // Poll for results if processing
  useEffect(() => {
    let resultsIntervalId;
    let progressIntervalId;
    
    if (polling && filterId) {
      // Poll for completed results every 3 seconds
      resultsIntervalId = setInterval(() => {
        fetchResults(false);
      }, 3000);
      
      // Poll for progress more frequently (every 1 second) for smoother updates
      progressIntervalId = setInterval(async () => {
        try {
          // Ensure we have a valid token
          const { data } = await supabase.auth.getSession();
          if (!data?.session?.access_token) {
            await supabase.auth.refreshSession();
          }
          
          // Fetch progress data
          const response = await apiGet(`/api/progress/${filterId}`);
          if (response.ok) {
            const progressData = await response.json();
            if (setFilterProgress) {
              setFilterProgress(progressData);
            }
          }
        } catch (error) {
          console.error('Error fetching progress:', error);
        }
      }, 1000);
    }
    
    return () => {
      if (resultsIntervalId) {
        clearInterval(resultsIntervalId);
      }
      if (progressIntervalId) {
        clearInterval(progressIntervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polling, filterId]);

  // Check if should show tutorial on mount
  useEffect(() => {
    const checkTutorial = async () => {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) return;
      
      const { data } = await supabase
        .from('user_profiles')
        .select('hide_list_cleaner_results_tutorial')
        .eq('id', user.id)
        .single();
      
      // Show tutorial if not hidden
      if (!data?.hide_list_cleaner_results_tutorial) {
        setTimeout(() => setShowTour(true), 500);
      }
    };
    
    checkTutorial();
  }, []);

  // Handle tutorial finish
  const handleTourFinish = async () => {
    setShowTour(false);
    
    // Save preference if "don't show again" is checked
    if (dontShowAgain) {
      const user = (await supabase.auth.getUser()).data.user;
      if (user) {
        await supabase
          .from('user_profiles')
          .update({ hide_list_cleaner_results_tutorial: true })
          .eq('id', user.id);
      }
    }
  };

  // Fetch filter results from the API
  const fetchResults = async (showLoading = true) => {
    // OVERRIDE FIX: Enhanced check to ensure we always prioritize the saved filter ID
    const applySavedFilterMode = localStorage.getItem('applyingSavedFilter') === 'true';
    const existingFilterId = localStorage.getItem('currentFilterId');
    // Always use existing filter when in saved filter mode, regardless of current filterId
    const usingExistingFilterId = applySavedFilterMode && existingFilterId;
    
    console.log('OVERRIDE-FLOW:', `fetchResults check for saved filter mode:`, {
      applySavedFilterMode,
      existingFilterId,
      currentFilterId: filterId,
      usingExistingFilterId
    });
    
    if (usingExistingFilterId) {
      console.log('OVERRIDE-FLOW:', `Using existing filter ID from saved filter: ${existingFilterId}`);
      // Store the effective filter ID we're actually using
      const effectiveFilterId = existingFilterId;
      
      // Track which filter ID is actually in use for debugging purposes
      if (filterId && filterId !== effectiveFilterId) {
        console.log('OVERRIDE-FLOW:', `IMPORTANT: Overriding new filter ID ${filterId} with saved filter ID ${effectiveFilterId}`);
      }
      
      // Set this filter ID as a global variable that will be used in subsequent operations
      localStorage.setItem('currentFilterInUse', effectiveFilterId);
      
      // We need to use a consistent API approach to avoid inconsistencies
      setLoading(true);
      
      try {
        // Make direct API call to get results for the effective filter ID
        const response = await apiGet(`/api/results/${effectiveFilterId}`);
        
        // Always clean up the flag once we've handled it
        localStorage.removeItem('applyingSavedFilter');
        
        if (!response.ok) {
          throw new Error('Failed to load results for saved filter');
        }
        
        const responseData = await response.json();
        setLoading(false);
        
        if (responseData.status === 'processing') {
          // Job is still processing - set polling flag and clear any errors
          setPolling(true);
          setError(null);
          console.log(`OVERRIDE-FLOW: Filter ${effectiveFilterId} is still processing - polling for updates`);
        } else {
          // Job is complete - stop polling and set results
          setPolling(false);
          setFilterResults(responseData);
          console.log(`OVERRIDE-FLOW: Successfully loaded saved filter ${effectiveFilterId}`);
        }
      } catch (error) {
        setLoading(false);
        console.error('OVERRIDE-FLOW: Error fetching saved filter results:', error);
        setError(error.message || 'Failed to load saved filter results');
      }
      
      return; // Exit early - we're handling this separately
    }
    
    // Normal flow - check if we have a filter ID to fetch
    if (!filterId) {
      console.log('OVERRIDE-FLOW: No filter ID available, skipping fetch');
      return;
    }
    
    // Ensure we have a valid token before proceeding
    const ensureValidToken = async () => {
      const { data } = await supabase.auth.getSession();
      if (!data?.session?.access_token) {
        console.warn('[AUTH-DEBUG] No valid session before making fetch request');
        // Attempt to refresh the session
        await supabase.auth.refreshSession();
        console.log('[AUTH-DEBUG] Session refreshed');
      } else {
        console.log('[AUTH-DEBUG] Valid token confirmed before making request');
      }
    };
    
    await ensureValidToken();
    
    // RACE CONDITION FIX: Track attempt count for current filterId
    fetchAttemptRef.current += 1;
    const currentAttempt = fetchAttemptRef.current;
    
    // Add detailed logging to help debug race conditions
    console.log(`[FRONTEND-DEBUG] Fetching results for ${filterId}, attempt #${currentAttempt}`);
    console.log(`[FRONTEND-DEBUG] Polling status: ${polling ? 'ACTIVE' : 'INACTIVE'}`);
    console.log(`[FRONTEND-DEBUG] Job creation timestamp: ${filterId.replace('filter_', '')}`);
    
    // If this is a filterId newly created within the last second, implement a small delay
    // This gives the backend time to properly create the job before frontend tries to fetch
    if (currentAttempt === 1 && filterId.startsWith('filter_')) {
      const jobTimestamp = parseInt(filterId.replace('filter_', ''));
      const now = Date.now();
      const msSinceCreation = now - jobTimestamp;
      
      console.log(`[FRONTEND-DEBUG] Job age: ${msSinceCreation}ms`);
      
      // For very new jobs, add a small delay before first fetch to prevent race condition
      if (msSinceCreation < 300) {
        const delayMs = 300 - msSinceCreation;
        console.log(`[FRONTEND-DEBUG] Recent job detected, adding ${delayMs}ms delay before first fetch`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
    
    try {
      if (showLoading) {
        setLoading(true);
      }
      
      // Use authenticated API call
      const response = await apiGet(`/api/results/${filterId}`);
      console.log(`[FRONTEND-DEBUG] API response status:`, response.status); 
      
      if (showLoading) {
        setLoading(false);
      }
      
      if (!response.ok) {
        // RACE CONDITION FIX: Additional check for very recent jobs (<1 second old)
        // Even if we get a 404, we should keep polling for a short time if job is very new
        if (response.status === 404 && filterId.startsWith('filter_')) {
          const jobTimestamp = parseInt(filterId.replace('filter_', ''));
          const now = Date.now();
          const msSinceCreation = now - jobTimestamp;
          
          if (msSinceCreation < 1000) {
            console.log(`[FRONTEND-DEBUG] 404 for very recent job (${msSinceCreation}ms old), treating as 'processing'`);
            setPolling(true);
            setError(null);
            return; // Exit early but keep polling
          }
        }
      
        const errorData = await response.json();
        // Handle error - might be authentication or not found
        if (response.status === 404) {
          // Filter not found - likely a session/auth issue
          throw new Error('Previous filter process was not found. Please configure your filters again.');
        } else {
          // Other errors
          throw new Error(errorData.error || `Failed with status: ${response.status}`);
        }
      }
      
      const responseData = await response.json();
      console.log(`[FRONTEND-DEBUG] Response data:`, responseData);
      
      // FIXED: The race condition issue - properly handle processing status
      if (responseData.status === 'processing') {
        // Job is still processing - set polling flag and clear any errors
        setPolling(true);
        setError(null); // Clear any existing errors
        
        // Show processing status message instead of error
        console.log(`[FRONTEND-DEBUG] Filter job ${filterId} is still processing - polling for updates`);
        // If we have progress information, display it
        if (responseData.progress) {
          console.log(`[FRONTEND-DEBUG] Progress: ${responseData.progress.progress}%, Status: ${responseData.progress.status}`);
        }
      } else {
        // Job is complete - stop polling and set results
        console.log(`[FRONTEND-DEBUG] Job complete, storing results`);
        setPolling(false);
        
        // Set filter results with safety check
        if (typeof setFilterResults === 'function') {
          setFilterResults(responseData);
        } else {
          console.warn('setFilterResults is not a function:', typeof setFilterResults);
        }
        
        // Store the current filter ID in localStorage for continuity
        if (filterId) {
          console.log('FILTER-ID-CONTINUITY: Storing current filter ID in localStorage:', filterId);
          localStorage.setItem('currentFilterInUse', filterId);
        }
      }
    } catch (error) {
      if (showLoading) {
        setLoading(false);
      }
      console.error('[FRONTEND-DEBUG] Error fetching results:', error);
      
      // RACE CONDITION FIX: For very new jobs, if we get a not found error, assume it's processing
      if (error.message?.includes('not found') && filterId.startsWith('filter_')) {
        const jobTimestamp = parseInt(filterId.replace('filter_', ''));
        const now = Date.now();
        const msSinceCreation = now - jobTimestamp;
        
        if (msSinceCreation < 1000) {
          console.log(`[FRONTEND-DEBUG] Error for very recent job (${msSinceCreation}ms old), treating as 'processing'`);
          setPolling(true);
          setError(null);
          return; // Exit early but keep polling
        }
      }
      
      // Only set error and redirect if this is NOT happening during background polling
      // This prevents the race condition where a "not found" error might appear during processing
      if (!polling || showLoading) {
        setError(error.message || 'Failed to load filter results. Please try again.');
        
        // If filter wasn't found and this is not a background polling call,
        // redirect back to filtering screen, but with safeguards for very new filters
        if (error.message?.includes('not found') && showLoading) {
          const isVeryNewJob = filterId.startsWith('filter_') && 
                               (Date.now() - parseInt(filterId.replace('filter_', ''))) < 2000;
          
          if (!isVeryNewJob) {
            console.log(`[FRONTEND-DEBUG] Job not found and not a new job, redirecting to filter config`);
            // Show alert to better inform user
            alert('Previous filter process was not found. Please configure your filters again.');
            onBack(); // Go back to previous step
          } else {
            console.log(`[FRONTEND-DEBUG] Not redirecting for new job, waiting for processing`);
            setPolling(true); // Force polling for new jobs
          }
        }
      }
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    setPage(0); // Reset to first page when changing tabs
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSortChange = (event) => {
    setSortBy(event.target.value);
    setPage(0);
  };

  // Handle frequency column header click for sorting
  const handleFrequencySort = () => {
    if (sortBy === 'frequency') {
      // Toggle sort order if already sorting by frequency
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      // Start sorting by frequency in descending order (highest first)
      setSortBy('frequency');
      setSortOrder('desc');
    }
    setPage(0);
  };

  // Calculate logic-aware stats based on AND/OR logic and local overrides
  const calculateLogicAwareStats = () => {
    if (!filterResults || !filterResults.keptData || !filterResults.excludedData) {
      return {
        totalRows: 0,
        qualifiedCount: 0,
        disqualifiedCount: 0
      };
    }

    // If we don't have logic mode or selected columns, fall back to raw counts
    const logicMode = filterResults.logicMode || 'AND';
    const selectedColumns = filterResults.selectedColumns || [];
    const llmValueDecisions = filterResults.llmValueDecisions || {};

    if (!selectedColumns.length) {
      return {
        totalRows: filterResults.stats?.totalRows || 0,
        qualifiedCount: filterResults.stats?.keptRowCount || 0,
        disqualifiedCount: filterResults.stats?.excludedRowCount || 0
      };
    }

    // Get all original data (kept + excluded)
    const allData = [...(filterResults.keptData || []), ...(filterResults.excludedData || [])];
    
    let qualifiedCount = 0;
    let disqualifiedCount = 0;

    // Apply logic to each row
    allData.forEach(row => {
      let keepRow = logicMode === 'OR' ? false : true; // Start with false for OR, true for AND

      selectedColumns.forEach(column => {
        const value = row[column];
        
        // Check for local override first
        const overrideKey = `${column}|${value}`;
        const hasLocalOverride = localOverrides[overrideKey];
        
        // Get decision (override or original)
        const decision = hasLocalOverride || 
          (llmValueDecisions[column] && llmValueDecisions[column][value] ? 
           llmValueDecisions[column][value].decision : 'EXCLUDE');

        // Apply logic
        if (logicMode === 'AND') {
          // AND logic: any EXCLUDE decision disqualifies the row
          if (decision === 'EXCLUDE') {
            keepRow = false;
          }
        } else if (logicMode === 'OR') {
          // OR logic: any KEEP decision qualifies the row
          if (decision === 'KEEP') {
            keepRow = true;
          }
        }
      });

      if (keepRow) {
        qualifiedCount++;
      } else {
        disqualifiedCount++;
      }
    });

    return {
      totalRows: allData.length,
      qualifiedCount,
      disqualifiedCount
    };
  };

  // Handle save filter button click
  const handleSaveClick = () => {
    // If we don't have a valid standardizedFilterConfig yet but we do have filterResults,
    // build it from the results data directly
    if ((!standardizedFilterConfig || !standardizedFilterConfig.selectedColumns || standardizedFilterConfig.selectedColumns.length === 0) && filterResults) {
      console.log('Creating standardizedFilterConfig from filterResults before opening save dialog');
      
      // Get column names from llmValueDecisions - single source of truth for columns
      const columnsFromLLM = filterResults.llmValueDecisions ? Object.keys(filterResults.llmValueDecisions) : [];
      console.log('Using columns directly from llmValueDecisions for save dialog:', columnsFromLLM);
      
      // Get reasoningText and model from database data only - no localStorage fallback
      const reasoningText = filterResults?.reasoningText || '';
      const model = filterResults?.model || 'gpt-4o';
      const chunkSize = filterResults?.chunkSize || 300;
      
      if (!filterResults?.reasoningText) {
        console.warn('No reasoningText found in filterResults for save operation');
      }
      
      // Use database data only - no localStorage fallback
      const savedColumnConfig = filterResults?.columnConfig || {};
      if (!filterResults?.columnConfig) {
        console.warn('No columnConfig found in filterResults for save operation');
      }
      
      // Create column config using instructions from localStorage
      const columnConfig = {};
      columnsFromLLM.forEach(column => {
        columnConfig[column] = savedColumnConfig[column] || { instructions: '' };
      });
      
      // Create a minimal but valid config for saving
      const tempConfig = {
        selectedColumns: columnsFromLLM,
        reasoningText,
        model,
        chunkSize,
        columnConfig
      };
      
      console.log('Created temporary config for saving:', tempConfig);
      setStandardizedFilterConfig(tempConfig);
      setSaveDialogOpen(true);
      return;
    }
    
    // Standard path - we have valid config
    setSaveDialogOpen(true);
  };

  // Export results as CSV or Excel
  const handleExport = async (format) => {
    if (!filterId) {
      setError('No filter ID found. Please run a filter first.');
      return;
    }
    
    // Get the current filter ID from state to ensure we're using the most recent one
    const currentFilterId = filterId;
    console.log(`Exporting data for filter ID: ${currentFilterId} in ${format} format`);
    
    try {
      if (setLoading) setLoading(true);
      
      // Use authenticated fetch for blob response
      console.log(`Making export request to: /api/export/${currentFilterId}?format=${format}`);
      const response = await authenticatedFetch(`/api/export/${currentFilterId}?format=${format}`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        throw new Error(`Export failed with status: ${response.status}`);
      }
      
      // Get the blob data
      const blobData = await response.blob();
      
      // Extract filename from Content-Disposition header or use default
      let filename = `ListCleaner-filtered_data.${format}`;
      const contentDisposition = response.headers.get('Content-Disposition');
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, ''); // Remove quotes
        }
      }
      
      console.log(`Downloading file: ${filename}`);
      
      // Create a download link
      const url = window.URL.createObjectURL(blobData);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      if (setLoading) setLoading(false);
    } catch (error) {
      if (setLoading) setLoading(false);
      console.error('Export error:', error);
      if (setError) setError(error.message || 'Failed to export results. Please try again.');
    }
  };

  // Direct clicking on chips has replaced context menu approach
  
  // Handle direct click on decision chip to toggle
  const handleDecisionClick = (value, column, newDecision) => {
    // Call the existing override function with all necessary data directly
    handleOverrideDecision(newDecision, column, value);
  };

  // Handle override decision
  const handleOverrideDecision = async (decision, columnParam, valueParam) => {
    // Get the most accurate filter ID to use - prioritize props over potentially stale localStorage
    // This fixes issues where old localStorage values don't match current filter job
    const currentFilterInLocalStorage = localStorage.getItem('currentFilterInUse');
    let effectiveFilterId = filterId || currentFilterInLocalStorage;
    
    // CRITICAL FIX: If we detect a mismatch between props and localStorage, 
    // clear the saved filter flags and use the current filter ID from props
    if (filterId && currentFilterInLocalStorage && filterId !== currentFilterInLocalStorage.replace('filter-', '')) {
      console.log('OVERRIDE-FLOW:', `Detected filter ID mismatch - clearing saved filter flags`);
      localStorage.removeItem('applyingSavedFilter');
      localStorage.removeItem('currentFilterId');
      localStorage.removeItem('currentFilterInUse');
      effectiveFilterId = filterId;
    }
    
    console.log('OVERRIDE-FLOW:', `Override decision using filter ID:`, {
      fromProps: filterId,
      fromLocalStorage: currentFilterInLocalStorage,
      effectiveFilterId: effectiveFilterId
    });
    
    // Validate inputs
    if (!decision || !columnParam || valueParam === undefined || !effectiveFilterId) {
      console.error('Invalid override parameters or missing filter ID');
      return;
    }

    // Update local overrides immediately for instant UI feedback
    const overrideKey = `${columnParam}|${valueParam}`;
    setLocalOverrides(prev => ({
      ...prev,
      [overrideKey]: decision
    }));
    console.log('Local override applied immediately for instant UI feedback');

    try {
      console.log('OVERRIDE-FLOW:', `Processing ${decision} override on ${columnParam}`);
      
      // Submit override to API using authenticated POST (background save)
      // Create the proper overrides format expected by the backend
      // Use the effectiveFilterId to ensure continuity
      console.log('OVERRIDE-FLOW:', `Sending override request with filter ID: ${effectiveFilterId}`);
      const response = await apiPost('/api/overrides', {
        filterId: effectiveFilterId, // Use the effective filter ID here
        overrides: {
          column: columnParam,
          value: valueParam,
          decision
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Override failed with status: ${response.status}`);
      }

      // Get response data
      const responseData = await response.json();
      console.log('Background database save successful:', responseData);
      
      // Hide loading indicator if setLoading function exists
      if (setLoading) {
        setLoading(false);
      }
    } catch (error) {
      if (setLoading) {
        setLoading(false);
      }
      console.error('Background database save failed:', error);
      
      // Revert the local override since database save failed
      setLocalOverrides(prev => {
        const newOverrides = { ...prev };
        delete newOverrides[overrideKey];
        return newOverrides;
      });
      console.log('Reverted local override due to database save failure');
      
      setError(error.message || 'Failed to save decision to database. Please try again.');
    }
  };

  // Filter data based on search query for simple view tabs
  const getFilteredData = (data) => {
    if (!searchQuery.trim() || !data) return data;
    
    const query = searchQuery.toLowerCase().trim();
    
    return data.filter(row => {
      // Search across all fields in the row
      return Object.values(row).some(value => {
        if (value === null || value === undefined) return false;
        return String(value).toLowerCase().includes(query);
      });
    });
  };

  // Helper function to determine if a decision can be toggled and what the new value would be
  const getDecisionToggleInfo = (rowData) => {
    const currentDecision = rowData.decision;
    const isUserOverride = rowData.reason?.startsWith('User override');
    const newDecision = currentDecision === 'KEEP' ? 'EXCLUDE' : 'KEEP';
    
    return {
      currentDecision,
      isUserOverride,
      newDecision
    };
  };

  // Create rows for decisions grid
  const getDecisionGridRows = () => {
    if (!filterResults?.llmValueDecisions) return [];
    
    const rows = [];
    let id = 0;
    
    Object.entries(filterResults.llmValueDecisions).forEach(([column, values]) => {
      Object.entries(values).forEach(([value, info]) => {
        // Check for local override first
        const overrideKey = `${column}|${value}`;
        const hasLocalOverride = localOverrides[overrideKey];
        
        rows.push({
          id: id++,
          column,
          value,
          decision: hasLocalOverride || info.decision, // Use override if exists, otherwise original
          reason: hasLocalOverride ? `User override: ${hasLocalOverride}` : (info.reason || ''),
          frequency: info.frequency || 0
        });
      });
    });
    
    // Apply sorting
    if (sortBy) {
      rows.sort((a, b) => {
        let aValue, bValue;
        
        switch(sortBy) {
          case 'frequency':
            aValue = a.frequency || 0;
            bValue = b.frequency || 0;
            break;
          case 'column':
            aValue = a.column.toLowerCase();
            bValue = b.column.toLowerCase();
            break;
          case 'value':
            aValue = a.value.toLowerCase();
            bValue = b.value.toLowerCase();
            break;
          case 'decision':
            aValue = a.decision;
            bValue = b.decision;
            break;
          default:
            return 0;
        }
        
        // Apply sort order
        if (sortOrder === 'desc') {
          if (sortBy === 'frequency') {
            return bValue - aValue; // Numeric descending
          } else {
            if (aValue > bValue) return -1;
            if (aValue < bValue) return 1;
            return 0;
          }
        } else {
          if (sortBy === 'frequency') {
            return aValue - bValue; // Numeric ascending
          } else {
            if (aValue < bValue) return -1;
            if (aValue > bValue) return 1;
            return 0;
          }
        }
      });
    }
    
    return rows;
  };

  // Render loading or processing state with enhanced progress tracking
  if (!filterResults || polling) {
    return (
      <Box sx={{ position: 'relative', minHeight: '300px' }}>
        <LoadingOverlay 
          isLoading={true} 
          progress={filterProgress || {
            status: 'processing',
            message: 'Processing your data...',
            progress: 0,
            columnsInProgress: [],
            columnStatuses: {}
          }}
        />
      </Box>
    );
  }

  // Debug logging to understand the data structure
  console.log('[FilterResults Debug] filterResults structure:', {
    hasFilterResults: !!filterResults,
    hasStats: !!filterResults?.stats,
    stats: filterResults?.stats,
    hasKeptData: !!filterResults?.keptData,
    keptDataLength: filterResults?.keptData?.length,
    hasExcludedData: !!filterResults?.excludedData,
    excludedDataLength: filterResults?.excludedData?.length,
    hasLlmValueDecisions: !!filterResults?.llmValueDecisions,
    llmValueDecisionsKeys: filterResults?.llmValueDecisions ? Object.keys(filterResults.llmValueDecisions) : []
  });

  return (
    <Box sx={{ width: '100%' }}>
      {/* Add tutorial */}
      <ResultsPageTour 
        run={showTour} 
        onFinish={handleTourFinish}
        dontShowAgain={dontShowAgain}
        setDontShowAgain={setDontShowAgain}
      />
      
      {/* Header with Back Button */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
            '&:hover': {
              color: '#0095FF',
              borderColor: '#0095FF',
              backgroundColor: 'rgba(0, 149, 255, 0.1)'
            }
          }}
        >
          Back to Lists
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title="Export Excel with All Data (Kept, Excluded, Decisions)">
            <IconButton id="export-results-button" onClick={() => handleExport('xlsx')}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: 2,
        mb: 3,
        maxWidth: '800px',
        mx: 'auto'
      }}>
        <Card sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid #ff980030',
          borderRadius: 3
        }}>
{(() => {
            const logicAwareStats = calculateLogicAwareStats();
            
            return (
              <>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Total Rows
                  </Typography>
                  <Typography variant="h4" sx={{ color: '#ff9800' }}>
                    {logicAwareStats.totalRows}
                  </Typography>
                </CardContent>
              </>
            );
          })()}
        </Card>
        
        <Card sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid #0095FF30',
          borderRadius: 3
        }}>
          {(() => {
            const logicAwareStats = calculateLogicAwareStats();
            
            return (
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Qualified
                </Typography>
                <Typography variant="h4" sx={{ color: '#0095FF' }}>
                  {logicAwareStats.qualifiedCount}
                </Typography>
              </CardContent>
            );
          })()}
        </Card>
        
        <Card sx={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid #FF149330',
          borderRadius: 3
        }}>
          {(() => {
            const logicAwareStats = calculateLogicAwareStats();
            
            return (
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Disqualified
                </Typography>
                <Typography variant="h4" sx={{ color: '#FF1493' }}>
                  {logicAwareStats.disqualifiedCount}
                </Typography>
              </CardContent>
            );
          })()}
        </Card>
      </Box>
      
      <Paper sx={{ 
        backgroundColor: 'background.paper',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        borderRadius: 3,
        mb: 3,
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%'
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
        }}>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            textColor="text.primary"
            aria-label="filter results tabs"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#9C27B0',
              },
              '& .Mui-selected': {
                color: 'text.primary',
                fontWeight: 600,
              }
            }}
          >
            <Tab label="Decisions" />
            <Tab label="Qualification Criteria" />
          </Tabs>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Column Filter Dropdown - Only show for Decisions tab */}
            {tabIndex === 0 && (
              <FormControl size="small" sx={{ minWidth: 140, maxWidth: 300 }}>
                <InputLabel>Filter Column</InputLabel>
                <Select
                  id="column-filter-dropdown"
                  value={columnFilter}
                  onChange={(e) => setColumnFilter(e.target.value)}
                  label="Filter Column"
                  sx={{
                    '& .MuiSelect-select': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }
                  }}
                >
                  <MenuItem value="all">All Columns</MenuItem>
                  {Object.keys(filterResults?.llmValueDecisions || {}).map(column => (
                    <MenuItem 
                      key={column} 
                      value={column}
                      title={column}
                      sx={{
                        maxWidth: '300px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {column}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              id="results-search-bar"
              size="small"
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: '250px' }}
            />
          </Box>
        </Box>
        
        <Box sx={{ p: 1 }}>
          {/* Decisions Tab - Shows value decisions */}
          {tabIndex === 0 && (
            <Box>
              {/* Column Headers - Same style as ResearchResults */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '150px 250px 120px 1fr 100px',
                gap: 1.5,
                p: 2,
                mb: 1,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: '1px solid rgba(255, 255, 255, 0.05)'
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Column</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Value</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, textAlign: 'center' }}>Decision</Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Reason</Typography>
                <Box 
                  id="frequency-column-header"
                  onClick={handleFrequencySort}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                      color: '#0095FF'
                    }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Frequency
                  </Typography>
                  {sortBy === 'frequency' && (
                    sortOrder === 'desc' ? 
                      <ArrowDownwardIcon sx={{ ml: 0.5, fontSize: 16 }} /> : 
                      <ArrowUpwardIcon sx={{ ml: 0.5, fontSize: 16 }} />
                  )}
                </Box>
              </Box>

              {/* Data Rows - Using Stack like ResearchResults */}
              <Stack spacing={0.25}>
                {(() => {
                  // Get and filter data
                  let filteredRows = getDecisionGridRows();
                  
                  if (columnFilter !== 'all') {
                    filteredRows = filteredRows.filter(row => row.column === columnFilter);
                  }
                  
                  if (searchQuery.trim()) {
                    const query = searchQuery.toLowerCase().trim();
                    filteredRows = filteredRows.filter(row => 
                      row.column.toLowerCase().includes(query) || 
                      row.value.toLowerCase().includes(query)
                    );
                  }
                  
                  // Paginate
                  const paginatedRows = filteredRows.slice(
                    page * rowsPerPage, 
                    page * rowsPerPage + rowsPerPage
                  );
                  
                  return paginatedRows.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      <Typography>
                        {searchQuery.trim() ? `No results found for "${searchQuery}"` : 'No data available'}
                      </Typography>
                    </Box>
                  ) : (
                    paginatedRows.map((row, index) => {
                      const { currentDecision, newDecision } = getDecisionToggleInfo(row);
                      
                      return (
                        <Paper
                          key={`${row.column}|${row.value}`}
                          sx={{
                            p: 0.75,
                            borderRadius: 2,
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.04)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                            }
                          }}
                        >
                          <Box sx={{ 
                            display: 'grid',
                            gridTemplateColumns: '150px 250px 120px 1fr 100px',
                            gap: 1,
                            alignItems: 'center'
                          }}>
                            <Tooltip title={row.column} arrow placement="top">
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 500, 
                                  fontSize: '0.8rem',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  cursor: 'help'
                                }}
                              >
                                {row.column}
                              </Typography>
                            </Tooltip>

                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                              {row.value}
                            </Typography>

                            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                              <Chip 
                                data-decision-chip="true"
                                label={currentDecision === 'KEEP' ? 'Qualified' : 'Disqualified'} 
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDecisionClick(row.value, row.column, newDecision);
                                }}
                                sx={{ 
                                  backgroundColor: currentDecision === 'KEEP' 
                                    ? 'rgba(0, 149, 255, 0.1)' 
                                    : 'rgba(255, 20, 147, 0.1)',
                                  color: currentDecision === 'KEEP' ? '#0095FF' : '#FF1493',
                                  fontWeight: 500,
                                  border: currentDecision === 'KEEP' 
                                    ? '1px solid #0095FF30' 
                                    : '1px solid #FF149330',
                                  height: '20px',
                                  fontSize: '0.65rem',
                                  cursor: 'pointer',
                                  '&:hover': { 
                                    backgroundColor: currentDecision === 'KEEP' 
                                      ? 'rgba(0, 149, 255, 0.2)' 
                                      : 'rgba(255, 20, 147, 0.2)'
                                  }
                                }}
                              />
                            </Box>

                            <Tooltip title={row.reason || ''} arrow placement="top">
                              <Typography 
                                variant="body2" 
                                color="text.secondary"
                                sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  cursor: 'help',
                                  fontSize: '0.75rem',
                                  lineHeight: 1.2
                                }}
                              >
                                {row.reason}
                              </Typography>
                            </Tooltip>

                            <Typography variant="body2" sx={{ fontSize: '0.75rem', textAlign: 'center' }}>
                              {row.frequency || 0}
                            </Typography>
                          </Box>
                        </Paper>
                      );
                    })
                  );
                })()}
              </Stack>

              {/* Pagination - Same style as ResearchResults */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                p: 2,
                borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                '& .MuiTablePagination-root': {
                  color: 'rgba(255, 255, 255, 0.7)'
                },
                '& .MuiTablePagination-select': {
                  color: 'rgba(255, 255, 255, 0.9)'
                },
                '& .MuiIconButton-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  '&.Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.3)'
                  }
                }
              }}>
                <TablePagination
                  component="div"
                  count={(() => {
                    let filteredRows = getDecisionGridRows();
                    if (columnFilter !== 'all') {
                      filteredRows = filteredRows.filter(row => row.column === columnFilter);
                    }
                    if (searchQuery.trim()) {
                      const query = searchQuery.toLowerCase().trim();
                      filteredRows = filteredRows.filter(row => 
                        row.column.toLowerCase().includes(query) || 
                        row.value.toLowerCase().includes(query)
                      );
                    }
                    return filteredRows.length;
                  })()}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100, 200]}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`} decisions`
                  }
                />
              </Box>
            </Box>
          )}
          
          {/* Qualification Criteria Tab - Shows the instructions and criteria used */}
          {tabIndex === 1 && (
            <Box>
              <Stack spacing={3}>
                {/* Product/Service Context and Logic Mode - Side by side */}
                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                  <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)', flex: '1 1 70%', minWidth: 0 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#0095FF' }}>Product/Service Context</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ 
                      color: (!filterResults?.reasoningText && !standardizedFilterConfig?.reasoningText) ? '#ff6b6b' : 'text.secondary' 
                    }}>
                      {filterResults?.reasoningText || standardizedFilterConfig?.reasoningText || '❌ Failed to retrieve product/service context from database'}
                    </Typography>
                  </Paper>

                  <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)', flex: '1 1 30%', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#0095FF' }}>Logic Mode</Typography>
                    <Chip 
                      label={filterResults?.logicMode || standardizedFilterConfig?.logicMode || 'AND'} 
                      sx={{ 
                        backgroundColor: 'rgba(0, 149, 255, 0.1)',
                        color: '#0095FF',
                        fontWeight: 600,
                        mb: 1
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                      {filterResults?.logicMode === 'OR' || standardizedFilterConfig?.logicMode === 'OR'
                        ? 'Row qualifies if ANY column meets its criteria'
                        : 'Row qualifies only if ALL columns meet their criteria'}
                    </Typography>
                  </Paper>
                </Box>

                {/* Column-specific Criteria */}
                <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(255, 255, 255, 0.05)' }}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#0095FF' }}>Column Criteria</Typography>
                  <Stack spacing={2}>
                    {(() => {
                      // Use database data first, no localStorage fallback
                      const columnConfig = filterResults?.columnConfig || standardizedFilterConfig?.columnConfig || {};
                      const selectedColumns = filterResults?.selectedColumns || standardizedFilterConfig?.selectedColumns || [];
                      
                      if (selectedColumns.length === 0) {
                        return <Typography variant="body2" sx={{ color: '#ff6b6b' }}>❌ Failed to retrieve column configuration from database</Typography>;
                      }
                      
                      return selectedColumns.map(column => {
                        const instructions = columnConfig[column]?.instructions || '';
                        const hasInstructions = instructions && instructions.trim() !== '';
                        return (
                          <Box key={column} sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.01)', borderRadius: 1, border: '1px solid rgba(255, 255, 255, 0.03)' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#FF1493', mb: 1 }}>
                              {column}
                            </Typography>
                            <Typography variant="body2" sx={{ 
                              color: hasInstructions ? 'text.secondary' : '#ff6b6b' 
                            }}>
                              {instructions || '❌ Failed to retrieve column instructions from database'}
                            </Typography>
                          </Box>
                        );
                      });
                    })()}
                  </Stack>
                </Paper>

              </Stack>
            </Box>
          )}
        </Box>
        
        {/* Context menu removed - direct chip clicking implemented */}
      </Paper>
      
      {/* Only show save button when we have results */}
      {filterResults && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
            id="save-template-button"
            variant="outlined" 
            onClick={handleSaveClick}
            startIcon={<BookmarkIcon />}
            sx={{ 
              textTransform: 'none', 
              borderRadius: '4px',
              fontWeight: 'medium',
              px: 4,
              py: 1.2,
              fontSize: '1rem',
              border: '1px solid #0095FF',
              color: '#0095FF',
              '&:hover': {
                borderColor: '#AB47BC',
                color: '#AB47BC'
              }
            }}
          >
            Save Template
          </Button>
        </Box>
      )}
      
      {/* Save Filter Dialog */}
      <SaveFilterDialog 
        open={saveDialogOpen} 
        onClose={() => setSaveDialogOpen(false)}
        filterId={filterId}
        filterConfig={standardizedFilterConfig}
        overrides={getCurrentOverrides()}
      />
    </Box>
  );
});

// Force redeploy: Added enhanced localStorage column instruction handling 
export default FilterResults;